/**
 * Global Collect&Go basket worker — syncs mapped products onto the Cogo cart
 * for open orders when the preference is enabled.
 */

import { isCogoTabBusy, probeCogoExtensionFast } from './cogoExtension'
import {
  isCogoBasketEnabled,
  subscribeCogoBasketEnabled,
} from './cogoBasketPrefs'
import { beginCogoAutomation, endCogoAutomation } from './cogoTabLifecycle'
import {
  runCogoSyncWorker,
  startCogoSync,
  type CogoSyncSnapshot,
} from './cogoSyncWorker'

/** Idle poll when queue empty, disabled, or waiting on extension/auth. */
const IDLE_MS = 20_000

let wakeIdle: (() => void) | null = null

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    let settled = false
    let kick: (() => void) | null = null
    const finish = () => {
      if (settled) return
      settled = true
      if (wakeIdle === kick) wakeIdle = null
      resolve()
    }
    const timer = setTimeout(finish, ms)
    kick = () => {
      clearTimeout(timer)
      finish()
    }
    wakeIdle = kick
  })
}

/** End the current idle sleep so the loop can react (e.g. preference on). */
function kickBasketLoop() {
  wakeIdle?.()
}

export type BasketQueueOrder = {
  orderId: number
  orderDisplay: string
  items: Array<{
    productId: number
    quantity: number
    display: string
    unit: string
  }>
}

export type CogoBasketPhase =
  | 'off'
  | 'idle'
  | 'waiting_ext'
  | 'busy_sync'
  | 'syncing'
  | 'waiting_choice'
  | 'error'

export type CogoBasketStatus = {
  enabled: boolean
  phase: CogoBasketPhase
  currentOrderId: number | null
  currentDisplay: string | null
  queueLength: number | null
  lastError: string | null
  updatedAt: number
}

const listeners = new Set<(s: CogoBasketStatus) => void>()

let status: CogoBasketStatus = {
  enabled: false,
  phase: 'off',
  currentOrderId: null,
  currentDisplay: null,
  queueLength: null,
  lastError: null,
  updatedAt: Date.now(),
}

function setStatus(patch: Partial<CogoBasketStatus>) {
  status = { ...status, ...patch, updatedAt: Date.now() }
  for (const cb of listeners) cb(status)
}

export function getCogoBasketStatus(): CogoBasketStatus {
  return status
}

export function subscribeCogoBasketStatus(
  cb: (s: CogoBasketStatus) => void
): () => void {
  listeners.add(cb)
  cb(status)
  return () => {
    listeners.delete(cb)
  }
}

export function formatCogoBasketLabel(s: CogoBasketStatus): string {
  if (!s.enabled) return 'Winkelwagen uit'
  if (s.phase === 'off') return 'Winkelwagen hmm'
  if (s.phase === 'syncing') {
    const name = s.currentDisplay?.trim()
    return name ? `Winkelwagen · ${name}` : 'Winkelwagen bezig…'
  }
  if (s.phase === 'waiting_choice') return 'Winkelwagen wacht (keuze)'
  if (s.phase === 'busy_sync') return 'Winkelwagen gepauzeerd (andere sync)'
  if (s.phase === 'waiting_ext') return 'Winkelwagen wacht (extensie/login)'
  if (s.phase === 'error' && s.lastError) {
    return `Winkelwagen fout · ${s.lastError}`
  }
  if (s.queueLength != null && s.queueLength > 0) {
    return `${s.queueLength} bestelling${s.queueLength === 1 ? '' : 'en'} klaarzetten`
  }
  return 'Winkelwagen klaar'
}

async function fetchQueue(): Promise<BasketQueueOrder[]> {
  try {
    const res = await fetch('/api/cogo/basket/queue', {
      credentials: 'include',
    })
    if (!res.ok) return []
    const data = (await res.json()) as { queue?: BasketQueueOrder[] }
    return Array.isArray(data.queue) ? data.queue : []
  } catch {
    return []
  }
}

/** Cancel paused/stale sync jobs and clear ok markers so products re-queue. */
async function resetBasketSyncState(): Promise<void> {
  try {
    await fetch('/api/cogo/basket/reset', {
      method: 'POST',
      credentials: 'include',
    })
  } catch {
    /* best-effort */
  }
}

let running = false
let abort: AbortController | null = null
let workerId = crypto.randomUUID()
let unsubEnabled: (() => void) | null = null
let syncAbort: AbortController | null = null

/**
 * Start the global basket loop (idempotent). Call from app shell when
 * the user is authenticated.
 */
export function startCogoBasketWorker() {
  if (running) return
  running = true
  abort = new AbortController()
  const signal = abort.signal
  setStatus({
    enabled: isCogoBasketEnabled(),
    phase: isCogoBasketEnabled() ? 'idle' : 'off',
  })

  unsubEnabled = subscribeCogoBasketEnabled((enabled) => {
    if (!enabled) {
      syncAbort?.abort()
      endCogoAutomation('basket')
      setStatus({
        enabled: false,
        phase: 'off',
        currentOrderId: null,
        currentDisplay: null,
        lastError: null,
      })
      return
    }
    // Turning on: drop paused/stale jobs and re-queue against current quantities.
    syncAbort?.abort()
    setStatus({
      enabled: true,
      phase: 'idle',
      currentOrderId: null,
      currentDisplay: null,
      lastError: null,
    })
    void resetBasketSyncState().finally(() => {
      kickBasketLoop()
    })
  })

  void (async () => {
    while (!signal.aborted) {
      try {
        if (!isCogoBasketEnabled()) {
          endCogoAutomation('basket')
          setStatus({
            enabled: false,
            phase: 'off',
            currentOrderId: null,
            currentDisplay: null,
          })
          await sleep(IDLE_MS)
          continue
        }

        const ext = await probeCogoExtensionFast()
        if (!ext.installed) {
          endCogoAutomation('basket')
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            currentOrderId: null,
            currentDisplay: null,
            queueLength: (await fetchQueue()).length,
          })
          await sleep(IDLE_MS)
          continue
        }

        const queue = await fetchQueue()
        if (queue.length === 0) {
          endCogoAutomation('basket')
          setStatus({
            enabled: true,
            phase: 'idle',
            currentOrderId: null,
            currentDisplay: null,
            queueLength: 0,
            lastError: null,
          })
          await sleep(IDLE_MS)
          continue
        }

        if (isCogoTabBusy() && !status.phase.startsWith('sync')) {
          // Another holder (manual sync / enrich) owns the tab — wait.
          setStatus({
            enabled: true,
            phase: 'busy_sync',
            queueLength: queue.length,
          })
          await sleep(IDLE_MS)
          continue
        }

        if (ext.loggingIn) {
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            queueLength: queue.length,
            lastError: 'needs_login',
          })
          await sleep(IDLE_MS)
          continue
        }

        // Start sync immediately — boot opens Cogo + auth check, then PDP per item.
        const next = queue[0]!
        beginCogoAutomation('basket')
        setStatus({
          enabled: true,
          phase: 'syncing',
          currentOrderId: next.orderId,
          currentDisplay: next.orderDisplay,
          queueLength: queue.length,
          lastError: null,
        })

        syncAbort?.abort()
        syncAbort = new AbortController()
        const linked = new AbortController()
        const onParentAbort = () => linked.abort()
        const onSyncAbort = () => linked.abort()
        signal.addEventListener('abort', onParentAbort)
        syncAbort.signal.addEventListener('abort', onSyncAbort)
        const snapBox: { current: CogoSyncSnapshot | null } = { current: null }

        try {
          // replace: don't get stuck on a paused job — always boot fresh.
          await startCogoSync(next.orderId, next.items, workerId, {
            replace: true,
          })
          await runCogoSyncWorker({
            orderId: next.orderId,
            workerId,
            signal: linked.signal,
            onSnapshot: (snap) => {
              snapBox.current = snap
              if (snap.status === 'waiting_choice') {
                setStatus({
                  enabled: true,
                  phase: 'waiting_choice',
                  currentOrderId: snap.orderId,
                  currentDisplay: snap.currentDisplay || next.orderDisplay,
                  queueLength: queue.length,
                })
              } else if (snap.status === 'waiting_login') {
                setStatus({
                  enabled: true,
                  phase: 'waiting_ext',
                  currentOrderId: snap.orderId,
                  currentDisplay: snap.currentDisplay || next.orderDisplay,
                  lastError: 'needs_login',
                })
              } else if (snap.status === 'running') {
                setStatus({
                  enabled: true,
                  phase: 'syncing',
                  currentOrderId: snap.orderId,
                  currentDisplay: snap.currentDisplay || next.orderDisplay,
                  queueLength: queue.length,
                })
              }
            },
          })
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          if (/andere worker/i.test(msg)) {
            setStatus({
              enabled: true,
              phase: 'busy_sync',
              queueLength: queue.length,
              lastError: msg,
            })
            await sleep(IDLE_MS)
            continue
          }
          setStatus({
            enabled: true,
            phase: 'error',
            lastError: msg,
            queueLength: queue.length,
          })
          await sleep(IDLE_MS)
          continue
        } finally {
          signal.removeEventListener('abort', onParentAbort)
          syncAbort?.signal.removeEventListener('abort', onSyncAbort)
        }

        const lastSnap = snapBox.current
        if (lastSnap?.status === 'waiting_choice') {
          // User must pick in Menu — release tab so idle close can run.
          endCogoAutomation('basket')
          setStatus({
            enabled: true,
            phase: 'waiting_choice',
            currentOrderId: next.orderId,
            currentDisplay: next.orderDisplay,
          })
          await sleep(IDLE_MS * 2)
          continue
        }
        if (lastSnap?.status === 'waiting_login') {
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            lastError: 'needs_login',
          })
          await sleep(IDLE_MS * 2)
          continue
        }
        if (lastSnap?.status === 'error') {
          setStatus({
            enabled: true,
            phase: 'error',
            lastError: lastSnap.lastError,
          })
          await sleep(IDLE_MS)
          continue
        }

        setStatus({
          enabled: isCogoBasketEnabled(),
          phase: isCogoBasketEnabled() ? 'idle' : 'off',
          currentOrderId: null,
          currentDisplay: null,
          queueLength: (await fetchQueue()).length,
          lastError: null,
        })
        // Next order's navigations share the extension 50/min gate with enrich.
      } catch {
        await sleep(IDLE_MS)
      }
    }
    endCogoAutomation('basket')
  })()
}

export function stopCogoBasketWorker() {
  abort?.abort()
  abort = null
  syncAbort?.abort()
  syncAbort = null
  running = false
  unsubEnabled?.()
  unsubEnabled = null
  kickBasketLoop()
  endCogoAutomation('basket')
  setStatus({
    phase: 'off',
    currentOrderId: null,
    currentDisplay: null,
  })
}
